// Kill Sheet — surface-line schedule row (Module 4.1a, display layer).
//
// Produces ONE synthetic Driller's Schedule row that is inserted at the
// start of the selected method's kill-mud pumping stage, immediately before
// the drill-string step-down begins at stroke zero. Used by BOTH the
// on-screen table (KillSheetOpEditor) and the printed table
// (KillSheetOpPrint) so paper and screen cannot drift apart.
//
// It is display-only. `computeDrillersSchedule()` and every pressure,
// stroke and time formula are untouched: the engine's stroke axis still
// starts at 0 = kill mud leaving surface for the top of the drill string,
// and `strokesToBit` still comes from the DRILL-STRING internal volume
// alone. The surface-line strokes sit before that zero and are reported
// separately here — they are never added to a schedule row's stroke value.
//
// No pressure target is invented for this row: the standpipe pressure
// while displacing the surface lines is not an output of any existing
// calculation, so the cell reads "—".

import { minutesForStrokes } from "@/lib/openg/formulas/volumes";
import {
  resolveCard4SurfaceVolume, computeSurfaceStageTarget,
} from "@/lib/killsheet/surfaceDisplacement";

export const SURFACE_LINE_ROW_LABEL =
  "Surface lines: pump kill mud to the top of the drill string.";

export const SURFACE_LINE_RESET_REMINDER =
  "RESET STROKE COUNTER TO ZERO AFTER SURFACE-LINE DISPLACEMENT, BEFORE STARTING THE DRILL-STRING STEPDOWN SCHEDULE.";

export const DRILL_STRING_ZERO_LABEL =
  "Kill mud at top of drill string · drill-string schedule starts.";

/**
 * buildSurfaceLineRow({ rigId, pumpOutputBblStk, pumpRateSpm })
 *
 * Returns null only when there is no rig to read from at all; otherwise it
 * always returns a row object so the schedule states the position honestly
 * instead of silently omitting it:
 *   { ok, message, volumeValue, volumeUnit, volumeBbl,
 *     strokes, wholeStrokes, timeMin, sourceNote }
 * `strokes` keeps full precision (the caller rounds for display only) and
 * `wholeStrokes` is the whole-stroke completion figure, kept distinct.
 */
export function buildSurfaceLineRow({ rigId, pumpOutputBblStk, pumpRateSpm }) {
  if (!rigId) {
    return {
      ok: false,
      message: "No rig associated with this kill sheet — Surface Line Volume unavailable.",
      volumeValue: null, volumeUnit: "", volumeBbl: null,
      strokes: null, wholeStrokes: null, timeMin: null,
      sourceNote: "Rig Data Card 4",
    };
  }

  const source = resolveCard4SurfaceVolume(rigId);
  const base = {
    volumeValue: source.sourceValue,
    volumeUnit: source.sourceUnit,
    volumeBbl: source.volumeBbl,
    strokes: null, wholeStrokes: null, timeMin: null,
    sourceNote: "Rig Data Card 4",
  };

  if (source.state === "missing-rig-sheet") {
    return { ...base, ok: false, message: "Associated Rig Data Sheet not found." };
  }
  if (source.state === "missing-value") {
    return { ...base, ok: false, message: "Surface Line Volume not entered on Rig Data Card 4 (not treated as zero)." };
  }
  if (source.state === "invalid") {
    return { ...base, ok: false, message: "Surface Line Volume on Rig Data Card 4 is invalid." };
  }

  const target = computeSurfaceStageTarget({
    volumeBbl: source.volumeBbl,
    pumpOutputBblStk,
  });
  if (!target.ok) {
    return { ...base, ok: false, message: target.message };
  }

  const minutes = minutesForStrokes(target.targetStrokes, pumpRateSpm);
  return {
    ...base,
    ok: true,
    message: "",
    strokes: target.targetStrokes,
    wholeStrokes: target.wholeStrokeTarget,
    timeMin: Number.isFinite(minutes) ? minutes : null,
  };
}

/** Index of the first row of the method's kill-mud pumping stage. */
export function firstKillRowIndex(rows) {
  if (!Array.isArray(rows)) return -1;
  return rows.findIndex((r) => r.phase === "kill");
}
